import TimezoneSelect from "react-timezone-select";
import { Check } from "lucide-react";
import timezones from "@/constants/Timezones";
import { defaultTimezoneSelectStyles } from "@/components/common/TimezoneSelector.styles";

/**
 * Timezone selector backed by the shared timezones list in @/constants/Timezones.
 *
 * Includes default styling via {@link defaultTimezoneSelectStyles}.
 *
 * To override specific style keys, import {@link defaultTimezoneSelectStyles},
 * spread it, and pass the modified object to the `styles` prop.
 * Alternatively, pass a new object to completely replace all default styles.
 *
 * @param {string} props.value - IANA timezone string (e.g. "America/New_York"). The displayed
 *   label is auto-generated by react-timezone-select as "(GMT{offset}) {label}", where the label
 *   comes from @/constants/Timezones and the offset is calculated from the IANA key at render time
 *   (e.g. "(GMT-4:00) Eastern Time (US & Canada)").
 * @param {function} props.onChange - Called with the react-select option object {value, label}
 * @param {"value"|"key"} [props.labelSource="value"] - Controls the label displayed in the
 *   dropdown and control. "value" shows the label from @/constants/Timezones (e.g. "Eastern Time
 *   (US & Canada)"); "key" shows the IANA key (e.g. "America/New_York").
 */
export default function TimezoneSelector({
  value,
  onChange,
  labelSource = "value",
  styles = defaultTimezoneSelectStyles,
  ...props
}) {
  return (
    <TimezoneSelect
      {...props}
      value={value}
      onChange={onChange}
      timezones={timezones}
      formatOptionLabel={(option, { context, selectValue } = {}) => {
        if (typeof option === "string") return option;
        const label = labelSource === "key" ? option.value : option.label;
        if (context === "value") return label;
        const isSelected = selectValue?.some((v) => v.value === option.value);
        return (
          <div className="flex w-full items-center justify-between gap-2">
            <span>{label}</span>
            <span className="flex size-4 shrink-0 items-center justify-center">
              {isSelected && <Check className="size-4 pointer-events-none" />}
            </span>
          </div>
        );
      }}
      styles={styles}
    />
  );
}
